import { Injectable, OnDestroy } from '@angular/core';
import { AlertController, Platform } from '@ionic/angular';
import { NGXLogger } from 'ngx-logger';
import { TEmployeeRole, TUnifiedLoginAppsEnvironmentVars } from '@monorepo/types';
import { UnifiedLoginDataService } from './unified-login-data.service';
import {
  TUserLoginStatus,
  TUserLoggedInInfo,
  TUserLoggedInInfoWithFeatureSet,
  TPTUserLoggedInInfo,
  TUnifiedLoginAppType,
  TMMUserLoggedInInfoWithFeatureSet,
  TLoginStatusType,
} from './unified-login.types';
import { Capacitor } from '@capacitor/core';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { UnifiedLoginEnvService } from './unified-login-env.service';
import { CapacitorHttpService } from '../capacitor/capacitor-http.service';
import { UnifiedLoginStorageService } from './unified-login-storage.service';
import { SplashScreen } from '@capacitor/splash-screen';
import { BehaviorSubject } from 'rxjs';

type TSessionCleanupHandler = () => void | Promise<void>;

@Injectable({ providedIn: 'root' })
export class UnifiedLoginService implements OnDestroy {
  loginProxyFromUrl: string | false = false;
  #userInfo: TUserLoggedInInfoWithFeatureSet | TMMUserLoggedInInfoWithFeatureSet = {
    userInfo: DUMMY_USER,
    featureSet: [],
    appType: null,
    loginStatus: 'UNKNOWN',
  };
  loginTimeMillis = 0;
  private loginStatusSubject = new BehaviorSubject<TLoginStatusType>(this.#userInfo.loginStatus);
  public loginStatus$ = this.loginStatusSubject.asObservable();
  #sessionTimout: ReturnType<typeof setTimeout> | null = null;
  #sessionCleanupHandlers = new Set<TSessionCleanupHandler>();

  constructor(
    private loginDataProvider: UnifiedLoginDataService,
    private unifiedLoginEnv: UnifiedLoginEnvService,
    private logger: NGXLogger,
    private platform: Platform,
    private alertController: AlertController,
    private capacitorHttpService: CapacitorHttpService,
    private inAppBrowser: InAppBrowser,
    private loggedInUserStorageService: UnifiedLoginStorageService,
  ) {
    this.#setLoginProxyFromUrl();
  }

  #setSessionTimeout() {
    // to avoid integer overflow, this is the largest 32 bit signed integer
    const MAX_DELAY = 0x7fffffff;
    const remaining = this.#getExpiryMillis() - Date.now();

    this.#clearSessionTimeout();

    if (remaining <= 0) {
      this.showTimeoutAlert();
    } else if (remaining > MAX_DELAY) {
      this.#sessionTimout = setTimeout(() => this.#setSessionTimeout(), MAX_DELAY);
    } else {
      this.#sessionTimout = setTimeout(() => this.showTimeoutAlert(), remaining);
    }
  }

  #clearSessionTimeout() {
    if (this.#sessionTimout !== null) {
      clearTimeout(this.#sessionTimout);
      this.#sessionTimout = null;
    }
  }

  async isUserAuthenticated(loginProxyFromURL: string | false): Promise<TUserLoginStatus> {
    if (loginProxyFromURL) {
      this.logger.log('Login proxy found', loginProxyFromURL);

      return this.fetchAndSetUser(loginProxyFromURL).then((r) => {
        switch (r) {
          case 'SUCCESS': {
            break;
          }
          case 'NO_PERMISSION': {
            this.showAlert('User does not have permission', 'Try logging in with another user', 'no-permission-popup');
            break;
          }
          default:
        }
        this.loginStatusSubject.next(this.#userInfo.loginStatus);
        return r;
      });
    } else {
      try {
        const userDTO = await this.getUserFromStorage();
        const appType = this.unifiedLoginEnv.getAppType();
        if (userDTO && userDTO.appType === appType) {
          this.logger.log('User session found!');

          if (Date.now() < userDTO.userInfo.keyExpiryInMillis) {
            this.setUserLoginDetails({ ...userDTO, loginStatus: 'EXISTING_SESSION' });
            this.loginStatusSubject.next(this.#userInfo.loginStatus);
            return Promise.resolve('SUCCESS');
          } else {
            this.showTimeoutAlert();
            return Promise.resolve('TIMEOUT');
          }
        } else {
          this.logger.log('User session not found. Redirecting back to login page.');
          this.#userInfo = {
            ...this.#userInfo,
            loginStatus: 'NO_SESSION',
          };
          this.loginStatusSubject.next(this.#userInfo.loginStatus);
          return Promise.resolve('FAILED');
        }
      } catch (error) {
        this.logger.log('User session not found. Redirecting back to login page.');
        this.#userInfo = {
          ...this.#userInfo,
          loginStatus: 'NO_SESSION',
        };
        this.loginStatusSubject.next(this.#userInfo.loginStatus);
        return Promise.resolve('FAILED');
      }
    }
  }

  showTimeoutAlert = () => {
    this.#userInfo = { ...this.#userInfo, loginStatus: 'SESSION_EXPIRED' };
    this.loginStatusSubject.next('SESSION_EXPIRED');

    this.showAlert(
      'Session Timeout!',
      'Please login again',
      'session-timeout-popup',
      '<img src="assets/common/to-clock.svg" alt="g-maps" class="clock-icon">',
    );
  };

  showAlert = (header: string, subHeader: string, cssClass: string, msg?: string) => {
    const alert = this.alertController.create({
      header,
      subHeader,
      cssClass,
      backdropDismiss: false,
      buttons: [
        {
          text: 'LOGIN',
          handler: () => {
            this.logoutWithRedirect();
          },
        },
      ],
      message: msg,
    });

    alert.then((a) => a.present());
  };

  setUserLoginDetails = (userDetails: TUserLoggedInInfoWithFeatureSet | TMMUserLoggedInInfoWithFeatureSet) => {
    // NOTE: Setting cookies again even after login as in iOS app
    // when app is restarted cookies are removed
    // setting cookes from local storage again
    this.#setCookieFromUserLoggedInInfo(userDetails.userInfo);
    this.loginTimeMillis = Date.now();
    this.loggedInUserStorageService.set('[LOGGED_IN_USER]', userDetails);
    this.#userInfo = userDetails;
    this.#setSessionTimeout();
  };

  /**
   * Registers a handler that is awaited before the login session is cleared
   * (explicit logout as well as session expiry).
   * @returns a function that unregisters the handler.
   */
  registerSessionCleanupHandler = (handler: TSessionCleanupHandler): (() => void) => {
    this.#sessionCleanupHandlers.add(handler);
    return () => this.#sessionCleanupHandlers.delete(handler);
  };

  #runSessionCleanupHandlers = async () => {
    const results = await Promise.allSettled([...this.#sessionCleanupHandlers].map(async (handler) => handler()));
    results.forEach((result) => {
      if (result.status === 'rejected') this.logger.error('Error while running session cleanup handler', result.reason);
    });
  };

  #cleanUserLoginDetails = async () => {
    await this.#runSessionCleanupHandlers();
    await this.loggedInUserStorageService.remove('[LOGGED_IN_USER]');

    this.#userInfo = { userInfo: DUMMY_USER, featureSet: [], appType: null, loginStatus: 'UNKNOWN' };
    this.loginTimeMillis = 0;
  };

  getUserFromStorage = async () => {
    const userDetails = await this.loggedInUserStorageService.get('[LOGGED_IN_USER]');
    return userDetails;
  };

  #setCookieFromUserLoggedInInfo(userInfo: TUserLoggedInInfo | TPTUserLoggedInInfo) {
    const accountGroupId = 'accountGroupId' in userInfo ? userInfo.accountGroupId : undefined;
    this.capacitorHttpService.setUserCookie(
      {
        username: userInfo.username,
        securityKey: userInfo.securityKey,
        factoryId: userInfo.factoryId,
        ...(accountGroupId ? { accountGroupId } : {}),
      },
      userInfo.keyExpiryInMillis,
    );
  }

  fetchAndSetUser = async (loginProxy: string) => {
    // Cleanup Old login Details
    await this.#cleanUserLoginDetails();
    // NOTE: if the login proxy in already decoded catch the error from atob
    // and use loginProxy as decodedLoginProxy
    let decodedLoginProxy = loginProxy;
    try {
      decodedLoginProxy = window.atob(loginProxy);
    } catch (e) {
      decodedLoginProxy = loginProxy;
    }

    let userSuccessfullySet: TUserLoginStatus = 'FAILED';

    try {
      const appType = this.unifiedLoginEnv.getAppType();
      const res = await this.loginDataProvider.fetchUserByLoginProxy(appType, decodedLoginProxy);
      const loginStatus: TLoginStatusType = 'UNKNOWN';
      switch (res.type) {
        case 'USER_LOGIN_SUCCESS_RESPONSE': {
          this.#setCookieFromUserLoggedInInfo(res);
          const userDetails = { userInfo: res, featureSet: [], appType, loginStatus };
          const validUserRoles = this.#getValidUserRolesFromAppType(appType);
          switch (appType) {
            case 'OI_APP': {
              const isValidUser =
                res?.roles?.length &&
                (res.roles.includes(validUserRoles[0]) ||
                  res.roles.includes(validUserRoles[1]) ||
                  res.roles.includes(validUserRoles[2]) ||
                  res.roles.includes(validUserRoles[3]));
              if (isValidUser) {
                this.setUserLoginDetails({ ...userDetails, loginStatus: 'FRESH_LOGIN' });
                userSuccessfullySet = 'SUCCESS';
              } else {
                this.logger.log(`User role is not in ${validUserRoles}.`);
                this.#userInfo = { userInfo: res, featureSet: [], appType, loginStatus: 'PERMISSION_DENIED' };
                userSuccessfullySet = 'NO_PERMISSION';
              }
              break;
            }

            case 'BA_APP': {
              const isValidUser = res.roles.includes(validUserRoles[0]);
              if (isValidUser) {
                this.setUserLoginDetails({ ...userDetails, loginStatus: 'FRESH_LOGIN' });
                userSuccessfullySet = 'SUCCESS';
              } else {
                this.logger.log(`User role is not in ${validUserRoles}.`);
                this.#userInfo = { userInfo: res, featureSet: [], appType, loginStatus: 'PERMISSION_DENIED' };
                userSuccessfullySet = 'NO_PERMISSION';
              }
              break;
            }
            case 'OT_APP': {
              const isValidUser = res.roles.includes(validUserRoles[0]);
              if (isValidUser) {
                this.setUserLoginDetails({ ...userDetails, loginStatus: 'FRESH_LOGIN' });
                userSuccessfullySet = 'SUCCESS';
              } else {
                this.logger.log(`User role is not in ${validUserRoles}.`);
                this.#userInfo = { userInfo: res, featureSet: [], appType, loginStatus: 'PERMISSION_DENIED' };
                userSuccessfullySet = 'NO_PERMISSION';
              }
              break;
            }
            case 'E8_APP': {
              const isValidUser = res.roles.includes(validUserRoles[0]);
              if (isValidUser) {
                this.setUserLoginDetails({ ...userDetails, loginStatus: 'FRESH_LOGIN' });
                userSuccessfullySet = 'SUCCESS';
              } else {
                this.logger.log(`User role is not in ${validUserRoles}.`);
                this.#userInfo = { userInfo: res, featureSet: [], appType, loginStatus: 'PERMISSION_DENIED' };
                userSuccessfullySet = 'NO_PERMISSION';
              }
              break;
            }

            default:
              if (res.permission && res.permission.length) {
                this.setUserLoginDetails({ ...userDetails, loginStatus: 'FRESH_LOGIN' });
                userSuccessfullySet = 'SUCCESS';
              } else {
                this.logger.log(`User don't have permission.`);
                this.#userInfo = { ...userDetails, loginStatus: 'PERMISSION_DENIED' };
                userSuccessfullySet = 'NO_PERMISSION';
              }
              break;
          }
          break;
        }
        case 'CUSTOM_PT_UNIFIED_LOGIN': {
          if (res.featureSet?.length) {
            this.setUserLoginDetails({
              userInfo: res.unifiedIdResponse,
              featureSet: res.featureSet,
              appType,
              loginStatus: 'FRESH_LOGIN',
            });
            userSuccessfullySet = 'SUCCESS';
          } else {
            userSuccessfullySet = 'NO_PERMISSION';
            this.#userInfo = {
              ...this.#userInfo,
              loginStatus: 'PERMISSION_DENIED',
            };
          }
          break;
        }
        case 'CUSTOM_MM_UNIFIED_LOGIN': {
          if (res.featureSet?.length) {
            this.setUserLoginDetails({
              userInfo: res.unifiedIdResponse,
              featureSet: res.featureSet,
              employeeRole: res.employeeRole as TEmployeeRole[number],
              appType,
              loginStatus: 'FRESH_LOGIN',
            });
            userSuccessfullySet = 'SUCCESS';
          } else {
            userSuccessfullySet = 'NO_PERMISSION';
            this.#userInfo = {
              ...this.#userInfo,
              loginStatus: 'PERMISSION_DENIED',
            };
          }
          break;
        }
      }
    } catch (e) {
      this.logger.error('Could not fetch user from login proxy', e);
    }

    return userSuccessfullySet;
  };

  #getValidUserRolesFromAppType(appType: TUnifiedLoginAppsEnvironmentVars['appType']): string[] {
    switch (appType) {
      case 'BA_APP':
        return ['DASHBOARD_APP_USER'];
      case 'OI_APP':
        return ['RPT_ADMIN', 'MM_RPT_ADMIN', 'PLANI8_RPT_ADMIN', 'OI_GROUP_MM_USER'];
      case 'OT_APP':
        return ['ORGANIZI8_AND_SKILLI8_APP_USER'];
      case 'E8_APP':
        return ['E8_APP_USER'];
      default:
        return [];
    }
  }

  getUserInfo = () => this.#userInfo.userInfo;

  getFactoryId = () => this.#userInfo.userInfo.factoryId;

  getUsername = () => this.#userInfo.userInfo.username;

  getWarehouseId = () => this.#userInfo.userInfo.warehouseId;

  getUserInfoByType = (userType: 'USER_LOGIN_SUCCESS_RESPONSE' = 'USER_LOGIN_SUCCESS_RESPONSE') => {
    if (this.#userInfo.userInfo.type === userType) return this.#userInfo.userInfo;
    throw new Error('Invalid type request for reqest for user ');
  };

  getFeatureSet = () => this.#userInfo.featureSet;

  getEmployeeRoleOpt = () => ('employeeRole' in this.#userInfo ? this.#userInfo.employeeRole : undefined);
  redirectToLogin = () => {
    const url = this.loginDataProvider.getLoginRedirectURL(this.unifiedLoginEnv.getAppType());
    if (Capacitor.isNativePlatform()) {
      this.#openInAppBrowser(url);
    } else {
      window.location.href = url;
    }
  };

  #openInAppBrowser(url: string) {
    const openUrl = url;
    console.log('Open Url', openUrl);
    const inAppBrowserRef = this.inAppBrowser.create(openUrl, '_blank', {
      location: 'no', // Hide Search Bar
      toolbar: 'no', //Hide Tool Bar
    });

    inAppBrowserRef.on('loadstop').subscribe((e) => {
      const pageUrl = new URL(e.url);
      const isSameLoginHost = pageUrl.host === this.unifiedLoginEnv.getEnvironmentConfig('unified_login');
      if (isSameLoginHost) {
        this.platform.ready().then(async () => {
          console.log('[iab] loadstop Hiding Spash Screen');
          await SplashScreen.hide();
        });
      }
    });
    inAppBrowserRef.on('loadstart').subscribe((e) => {
      const pageUrl = new URL(e.url);
      const host = pageUrl.host;
      const notLoginInitiate = !e.url.includes(
        // NOTE: path is hard coded here. This is the single place where this path is used.
        // can be moved to environment variable if there are more then one use case
        `${this.unifiedLoginEnv.HOST_DONAIN}/app_auth_services/ext/api/v1/login/initiate`,
      );
      const isSameHost = host === this.unifiedLoginEnv.HOST_DONAIN;
      console.log('[inAppBrowserRef] Load Start', e, this.unifiedLoginEnv.HOST_DONAIN, notLoginInitiate);
      const hasAuthTokenAndPath = pageUrl.searchParams.get('authToken') !== null && notLoginInitiate;
      const isDev = !!pageUrl.searchParams.get('isLocal');
      const doCloseinAppbrowser = hasAuthTokenAndPath && (isDev || isSameHost);
      if (doCloseinAppbrowser) {
        // Show spash screen again and close in app browser
        // Client application needs to hide the spash screen again when the app is rendered
        SplashScreen.show().then(() => {
          inAppBrowserRef.close();
          window.location.href = window.location.href + pageUrl.search;
        });
        inAppBrowserRef.close();
        window.location.href = window.location.href + pageUrl.search;
      }
    });
    inAppBrowserRef.on('loaderror').subscribe((e) => {
      const pageUrl = new URL(e.url);
      const isSameLoginHost = pageUrl.host === this.unifiedLoginEnv.HOST_DONAIN;
      console.log('[Load] Error', pageUrl, this.unifiedLoginEnv.HOST_DONAIN, isSameLoginHost);
      if (isSameLoginHost) {
        this.showAlert(
          'Problem Logging In!',
          'Please check your internet, and click login again',
          'session-timeout-popup',
          '<img src="assets/common/warning.svg" alt="g-maps" class="clock-icon">',
        );
      }
      inAppBrowserRef && inAppBrowserRef.close();
    });
    inAppBrowserRef.on('exit').subscribe((e) => {
      console.log('[IAB] exit', e);
      inAppBrowserRef && inAppBrowserRef.close();
    });
  }

  #getExpiryMillis = () => {
    return this.#userInfo ? this.#userInfo.userInfo.keyExpiryInMillis : Date.now();
  };

  #setLoginProxyFromUrl() {
    const authTokenKey = 'authToken';
    const params = new URLSearchParams(window.location.search);
    this.loginProxyFromUrl = params.get(authTokenKey) || false;
    params.delete(authTokenKey);
    params.delete('dev');
    const queryWithoutAuthToken = params.toString();
    // NOTE: Remove the ONLY authToken from the query param
    const query = queryWithoutAuthToken ? `?${queryWithoutAuthToken}` : '';
    history.replaceState(null, '', window.location.origin + window.location.pathname + query);
  }

  logoutWithRedirect() {
    this.logout().then(() => {
      this.redirectToLogin();
    });
  }

  logout = async () => {
    const userDetails = await this.getUserFromStorage();

    if (userDetails) {
      this.capacitorHttpService.clearAllCookies();
      const { securityKey, username, factoryId } = userDetails.userInfo;
      await this.loginDataProvider
        .invalidateSecurityKey({
          securityKey,
          username,
          factoryId,
        })
        .catch((e) => {
          this.logger.error('Error while invalidating security key', e);
        });
    }

    await this.#cleanUserLoginDetails();
  };

  /**
   * [Note]: This function is only for machine maintainance and plani8 app
   * @param appType: MM_APP
   * @param feature: MM Features
   * @returns boolean
   */

  isFeaturePermissionExist(
    appType: TUnifiedLoginAppType,
    feature: TUserLoggedInInfoWithFeatureSet['featureSet'][number],
  ) {
    switch (appType) {
      case 'MM_APP':
      case 'MM_APP_SOLVEI8':
      case 'PT_APP': {
        return this.#getFeaturesForTheUser(appType).includes(feature);
      }
      default: {
        return false;
      }
    }
  }

  #getFeaturesForTheUser(appType: TUnifiedLoginAppType) {
    switch (appType) {
      case 'MM_APP':
        return [...this.#userInfo.featureSet, ...CLIENT_MANAGED_FEATURE_CODES];
      case 'MM_APP_SOLVEI8': {
        return [...this.#userInfo.featureSet, ...CLIENT_MANAGED_FEATURE_CODES];
      }
      case 'PT_APP': {
        return this.#userInfo.featureSet;
      }
      default: {
        return [];
      }
    }
  }

  ngOnDestroy(): void {
    this.#clearSessionTimeout();
  }
}

const DUMMY_USER: TUserLoggedInInfo = {
  type: 'USER_LOGIN_SUCCESS_RESPONSE',
  keyExpiryInMillis: 0,
  factoryId: '',
  legacyJWT: '',
  roles: [],
  name: '',
  tenantId: '',
  permission: [],
  securityKey: '',
  moduleId: '',
  username: '',
  departmentId: '',
};

const CLIENT_MANAGED_FEATURE_CODES = [
  'HOME',
  'ASSET_MANAGEMENT',
  'FACTORY_MANAGEMENT',
  'MAINTENANCE_MANAGEMENT',
  'LINE_SET_UP',
  'TICKET_MANAGEMENT',
];
